import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, MapPin, MessageCircle, Users, Building2, Tag, Edit2, Save, X, Camera, Calendar, Phone, Mail } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHAPTER_DESCRIPTIONS = {
  "Durbanville": "Serving the Durbanville area and surrounds",
  "Brackenfell and Surrounds": "Connecting entrepreneurs in Brackenfell and nearby areas",
  "Helderberg": "The Helderberg region business community",
  "Stellenbosch": "Wine country's vibrant business network",
  "Boland": "Serving the greater Boland region",
  "Southern Peninsula": "From Muizenberg to Simon's Town",
  "Rondebosch and Surrounds": "Southern suburbs business community",
  "West Coast Neighbours": "The beautiful West Coast region",
  "Jozi": "Johannesburg's mom entrepreneur community"
};

export default function ChapterDetailPage({ user }) {
  const { chapterName } = useParams();
  const decodedChapterName = decodeURIComponent(chapterName);
  const fileInputRef = useRef(null);
  
  const [chapterData, setChapterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingHistory, setEditingHistory] = useState(false);
  const [historyText, setHistoryText] = useState('');
  const [editingWelcome, setEditingWelcome] = useState(false);
  const [welcomeText, setWelcomeText] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchChapterData();
  }, [decodedChapterName]);

  const fetchChapterData = async () => {
    try {
      const response = await axios.get(`${API}/chapters/${encodeURIComponent(decodedChapterName)}`);
      setChapterData(response.data);
      setHistoryText(response.data.info?.history || '');
      setWelcomeText(response.data.info?.welcome_message || '');
    } catch (error) {
      toast.error('Failed to load chapter data');
    } finally {
      setLoading(false);
    }
  };

  const canEditHistory = () => {
    if (!user || !chapterData) return false;
    if (user.is_admin) return true;
    if (user.is_area_manager) {
      const areas = user.managed_areas || [];
      return areas.includes(decodedChapterName) || user.managed_area === decodedChapterName;
    }
    return false;
  };

  const handleSaveHistory = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/chapters/${encodeURIComponent(decodedChapterName)}/info`, {
        history: historyText
      });
      toast.success('Chapter history updated!');
      setEditingHistory(false);
      fetchChapterData();
    } catch (error) {
      toast.error('Failed to update chapter history');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWelcome = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/chapters/${encodeURIComponent(decodedChapterName)}/info`, {
        welcome_message: welcomeText
      });
      toast.success('Welcome message updated!');
      setEditingWelcome(false);
      fetchChapterData();
    } catch (error) {
      toast.error('Failed to update welcome message');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/chapters/${encodeURIComponent(decodedChapterName)}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Chapter image updated!');
      fetchChapterData();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-[#64748b]">Loading chapter...</div>
      </div>
    );
  }

  if (!chapterData) {
    return (
      <div className="max-w-4xl mx-auto px-6 text-center py-12">
        <h1 className="text-2xl font-bold text-[#2d3748]">Chapter not found</h1>
        <Link to="/chapters" className="text-[#5b7f95] hover:underline mt-4 inline-block">
          Back to Chapters
        </Link>
      </div>
    );
  }

  const { leader, assistant, members, member_count, info, next_event } = chapterData;
  const chapterImage = info?.image ? getImageUrl(info.image) : null;

  return (
    <div className="max-w-6xl mx-auto px-6" data-testid="chapter-detail-page">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        className="hidden"
      />

      {/* Back Button */}
      <Link to="/chapters" className="inline-flex items-center gap-2 text-[#5b7f95] hover:text-[#4a6b80] mb-6">
        <ArrowLeft className="w-4 h-4" />
        Back to All Chapters
      </Link>

      {/* Chapter Header */}
      <div className="relative rounded-2xl overflow-hidden mb-8">
        {/* Background Image or Gradient */}
        {chapterImage ? (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${chapterImage})` }}
          >
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-r from-[#5b7f95] to-[#4a6b80]"></div>
        )}
        
        {/* Content */}
        <div className="relative p-8 text-white">
          <div className="flex items-start justify-between">
            <div className={chapterImage ? "bg-black/40 rounded-xl p-4 backdrop-blur-sm" : ""}>
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="w-6 h-6" />
                <h1 className="text-3xl font-bold">{decodedChapterName}</h1>
              </div>
              <p className="text-lg opacity-90 mb-4">{CHAPTER_DESCRIPTIONS[decodedChapterName]}</p>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                <span className="text-lg">{member_count} member{member_count !== 1 ? 's' : ''}</span>
              </div>
            </div>
            
            {/* Upload Button - only for chapter leader or admin */}
            {canEditHistory() && (
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                variant="outline"
                className="bg-white/20 border-white/40 text-white hover:bg-white/30 rounded-full"
              >
                <Camera className="w-4 h-4 mr-2" />
                {uploadingImage ? 'Uploading...' : 'Change Image'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Leader, Assistant & History */}
        <div className="lg:col-span-1 space-y-6">
          {/* Chapter Leader Card */}
          <Card className="border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-[#cb9944]/10 px-6 py-3 border-b border-gray-100">
              <h2 className="font-semibold text-[#2d3748]">Chapter Leader</h2>
            </div>
            <CardContent className="p-6">
              {leader ? (
                <div className="text-center">
                  <div className="relative h-24 w-24 mx-auto mb-4 rounded-full overflow-hidden bg-gray-100">
                    {leader.profile_image ? (
                      <img 
                        src={getImageUrl(leader.profile_image)}
                        alt={leader.display_name || leader.full_name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#cb9944] text-white text-2xl font-semibold">
                        {(leader.display_name || leader.full_name).charAt(0)}
                      </div>
                    )}
                  </div>
                  <h3 className="text-xl font-semibold text-[#2d3748]">
                    {leader.display_name || leader.full_name}
                  </h3>
                  {leader.business_name && (
                    <p className="text-[#5b7f95] font-medium mt-1">{leader.business_name}</p>
                  )}
                  {leader.business_category && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#5b7f95]/10 text-[#5b7f95] text-xs font-medium rounded-full mt-2">
                      <Tag className="w-3 h-3" />
                      {leader.business_category}
                    </span>
                  )}
                  {leader.business_description && (
                    <p className="text-sm text-[#64748b] mt-4 text-left">
                      {leader.business_description}
                    </p>
                  )}
                  {user && leader.id !== user.id && (
                    <Link to={`/messages/${leader.id}`}>
                      <Button className="w-full mt-6 bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full" data-testid="message-leader-btn">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Message {(leader.display_name || leader.full_name).split(' ')[0]}
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-[#64748b]">No chapter leader assigned yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assistant Leader Card */}
          {assistant && (
            <Card className="border-gray-100 rounded-2xl shadow-sm overflow-hidden">
              <div className="bg-[#cb9944]/5 px-6 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-[#2d3748]">Assistant Leader</h2>
              </div>
              <CardContent className="p-6">
                <div className="text-center">
                  <div className="relative h-20 w-20 mx-auto mb-4 rounded-full overflow-hidden bg-gray-100">
                    {assistant.profile_image ? (
                      <img 
                        src={getImageUrl(assistant.profile_image)}
                        alt={assistant.display_name || assistant.full_name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-[#cb9944]/70 text-white text-xl font-semibold">
                        {(assistant.display_name || assistant.full_name).charAt(0)}
                      </div>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-[#2d3748]">
                    {assistant.display_name || assistant.full_name}
                  </h3>
                  {assistant.business_name && (
                    <p className="text-[#5b7f95] font-medium mt-1 text-sm">{assistant.business_name}</p>
                  )}
                  {user && assistant.id !== user.id && (
                    <Link to={`/messages/${assistant.id}`}>
                      <Button variant="outline" className="w-full mt-4 border-[#cb9944] text-[#cb9944] hover:bg-[#cb9944]/10 rounded-full" data-testid="message-assistant-btn">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Message {(assistant.display_name || assistant.full_name).split(' ')[0]}
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Next Upcoming Meeting Card */}
          {next_event && (
            <Card className="border-gray-100 rounded-2xl shadow-sm overflow-hidden border-l-4 border-l-[#cb9944]">
              <div className="bg-[#cb9944]/5 px-6 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-[#2d3748] flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#cb9944]" />
                  Next Meeting
                </h2>
              </div>
              <CardContent className="p-6">
                <h4 className="font-semibold text-[#2d3748] mb-1">{next_event.title}</h4>
                <p className="text-sm text-[#64748b] mb-1">
                  {(() => { try { return format(new Date(next_event.date), 'PPP p'); } catch { return next_event.date; } })()}
                </p>
                {next_event.location && (
                  <p className="text-sm text-[#5b7f95] flex items-center gap-1 mb-3">
                    <MapPin className="w-3 h-3" /> {next_event.location}
                  </p>
                )}
                {next_event.is_paid && next_event.price && (
                  <p className="text-sm font-semibold text-[#cb9944] mb-3">R{next_event.price.toFixed(2)}</p>
                )}
                <Link to={`/events/${next_event.id}`}>
                  <Button className="w-full bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full" data-testid="book-meeting-btn">
                    <Calendar className="w-4 h-4 mr-2" />
                    View & Book
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Chapter History Card */}
          <Card className="border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-[#5b7f95]/10 px-6 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-semibold text-[#2d3748]">About This Chapter</h2>
              {canEditHistory() && !editingHistory && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setEditingHistory(true)}
                  className="text-[#5b7f95] hover:text-[#4a6b80]"
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            <CardContent className="p-6">
              {editingHistory ? (
                <div className="space-y-4">
                  <Textarea
                    value={historyText}
                    onChange={(e) => setHistoryText(e.target.value)}
                    placeholder="Share the history of this chapter, when it was founded, notable achievements, community involvement..."
                    rows={6}
                    className="bg-gray-50 border-gray-200 rounded-xl"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveHistory}
                      disabled={saving}
                      className="flex-1 bg-[#5b7f95] hover:bg-[#4a6b80] text-white rounded-full"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingHistory(false);
                        setHistoryText(info?.history || '');
                      }}
                      className="rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-[#64748b] whitespace-pre-wrap">
                  {info?.history || 'No chapter history available yet. The chapter leader can add information about this chapter.'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Chapter Welcome Message Card */}
          {canEditHistory() && (
            <Card className="border-gray-100 rounded-2xl shadow-sm overflow-hidden" data-testid="welcome-message-card">
              <div className="bg-[#cb9944]/10 px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-[#2d3748]">Welcome Message</h2>
                {!editingWelcome && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setEditingWelcome(true)}
                    className="text-[#cb9944] hover:text-[#b08435]"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <CardContent className="p-6">
                {editingWelcome ? (
                  <div className="space-y-4">
                    <p className="text-xs text-[#64748b]">This message is shown to new members when they first log in.</p>
                    <Textarea
                      value={welcomeText}
                      onChange={(e) => setWelcomeText(e.target.value)}
                      placeholder="Write a welcome note for new members joining this chapter..."
                      rows={4}
                      className="bg-gray-50 border-gray-200 rounded-xl"
                      data-testid="welcome-message-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveWelcome}
                        disabled={saving}
                        className="flex-1 bg-[#cb9944] hover:bg-[#b08435] text-white rounded-full"
                        data-testid="save-welcome-btn"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setEditingWelcome(false);
                          setWelcomeText(chapterData?.info?.welcome_message || '');
                        }}
                        className="rounded-full"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-[#64748b] whitespace-pre-wrap text-sm">
                    {chapterData?.info?.welcome_message || 'No welcome message set. Click edit to add one — new members will see it on their home page.'}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Members List */}
        <div className="lg:col-span-2">
          <Card className="border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-[#2d3748] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#5b7f95]" />
                Chapter Members ({member_count})
              </h2>
            </div>
            <CardContent className="p-6">
              {members && members.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {members.map((member) => {
                    const displayName = member.display_name || member.full_name;
                    const isLeader = leader && member.id === leader.id;
                    const isAssistant = assistant && member.id === assistant.id;
                    
                    return (
                      <Link
                        key={member.id}
                        to={`/directory?member=${member.id}`}
                        className="block"
                      >
                        <div 
                          className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer hover:shadow-sm transition-shadow ${isLeader ? 'border-[#cb9944] bg-[#cb9944]/5' : isAssistant ? 'border-[#cb9944]/50 bg-[#cb9944]/3' : 'border-gray-100 bg-gray-50'}`}
                          data-testid="chapter-member-card"
                        >
                          <div className="relative h-12 w-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                            {member.profile_image ? (
                              <img 
                                src={getImageUrl(member.profile_image)}
                                alt={displayName}
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className={`absolute inset-0 flex items-center justify-center ${isLeader ? 'bg-[#cb9944]' : 'bg-[#5b7f95]'} text-white font-semibold`}>
                                {displayName.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-[#2d3748] truncate">{displayName}</h4>
                              {isLeader && (
                                <span className="px-2 py-0.5 bg-[#cb9944] text-white text-xs rounded-full flex-shrink-0">
                                  Leader
                                </span>
                              )}
                              {isAssistant && (
                                <span className="px-2 py-0.5 bg-[#cb9944]/70 text-white text-xs rounded-full flex-shrink-0">
                                  Assistant
                                </span>
                              )}
                            </div>
                            {member.business_name && (
                              <p className="text-sm text-[#5b7f95] truncate flex items-center gap-1">
                                <Building2 className="w-3 h-3 flex-shrink-0" />
                                {member.business_name}
                              </p>
                            )}
                            {member.business_category && (
                              <p className="text-xs text-[#64748b] mt-1">{member.business_category}</p>
                            )}
                            {member.phone && (
                              <p className="text-xs text-[#64748b] mt-1 flex items-center gap-1">
                                <Phone className="w-3 h-3 flex-shrink-0" />
                                <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${member.phone}`; }} className="hover:text-[#5b7f95] cursor-pointer">{member.phone}</span>
                              </p>
                            )}
                            {(member.display_email || member.email) && (
                              <p className="text-xs text-[#64748b] mt-0.5 flex items-center gap-1 truncate">
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `mailto:${member.display_email || member.email}`; }} className="hover:text-[#5b7f95] truncate cursor-pointer">{member.display_email || member.email}</span>
                              </p>
                            )}
                          </div>
                          {user && member.id !== user.id && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="rounded-full border-[#5b7f95] text-[#5b7f95] hover:bg-[#5b7f95] hover:text-white flex-shrink-0"
                              data-testid="message-member-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.location.href = `/messages/${member.id}`;
                              }}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-[#64748b]">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No members in this chapter yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
